import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useDatasetsData } from '@pages/Transpiler/useDatasetsData';
import { ArrowNarrowUpRightIcon, EditBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Tooltip, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';

import { DatasetInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

export const DatasetsTable = ({
  isBulk,
  id,
  isSql,
  projectId: _projectId
}: {
  isBulk: boolean;
  id: string;
  isSql?: boolean;
  projectId?: string;
}) => {
  const { data, isError, isLoading } = useDatasetsData(id, isBulk);

  const datasetColumns: ColumnsType<DatasetInfoType> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search..',
          'data-test-id': 'datasets-table-name-search-input'
        }
      },
      render: (name) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={isSql ? Entity.Table : Entity.Dataset} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'source',
      dataIndex: 'source',
      sortable: true,
      title: 'Source File',
      render: renderColumnWithEllipsis
    },
    isSql
      ? {}
      : {
          key: 'pipelineName',
          dataIndex: 'pipelineName',
          title: 'Pipeline',
          sortable: true,
          render: (name) => {
            return (
              <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
                <EntityIcon entity={Entity.Pipeline} size='xs' />
                <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
              </Stack>
            );
          }
        },
    getStatusColumn(),
    {
      align: 'center',
      dataIndex: 'actions',
      key: 'actions',
      width: '50px',
      resizable: false,
      render: (_: string, rowData) => {
        if (isSql) {
          return;
        }
        const entityId = rowData.id;
        if (hasStatusWithError(rowData.status)) {
          return null;
        }
        let url = Private_Routes.Dataset.home.getUrl({ uid: entityId });
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
            <TableActions gap={theme.spaces.x12} alignY='center'>
              {(entityId || isSql) && (
                <TableIconButton
                  icon={<ArrowNarrowUpRightIcon type='default' />}
                  elementType='link'
                  to={url}
                  data-test-id='datasets-table-view-details-button'
                  aria-label={`View ${rowData.name} details`}
                />
              )}
              {rowData.pipelineId && !isSql && (
                <Tooltip title='Edit Pipeline'>
                  <div>
                    <TableIconButton
                      icon={<EditBIcon type='default' />}
                      elementType='link'
                      to={getIDEUrl({
                        uid: rowData.pipelineId,
                        entity: Entity.Pipeline
                      })}
                      data-test-id='datasets-table-edit-pipeline-button'
                      aria-label={`Edit pipeline for ${rowData.name}`}
                    />
                  </div>
                </Tooltip>
              )}
            </TableActions>
          </Stack>
        );
      }
    }
  ];

  const datasets = useMemo(() => data?.data?.datasets || [], [data?.data]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title={isSql ? 'Tables' : 'Datasets'} count={datasets.length} />
      <Stack grow='1'>
        <Table
          fixedHeader={true}
          height='100%'
          width='100%'
          loading={isLoading}
          virtualize={true}
          columns={datasetColumns}
          rowKey={'rowKey'}
          data-test-id='imported-info-datasets-table'
          dataSource={datasets.map((dataset, i) => ({ ...dataset, rowKey: i }))}
        />
      </Stack>
    </Stack>
  );
};
